import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Pencil, DollarSign, Download, Search, X, FileText, Upload, CheckSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ExportDropdown from '@/components/ExportDropdown';
import AccountingImportModal from '@/components/accounting/AccountingImportModal';
import { Checkbox } from '@/components/ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

const formatCurrency = (val) => `$${(val || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusColors = {
  unpaid: 'bg-amber-100 text-amber-800',
  partially_paid: 'bg-blue-100 text-blue-800',
  paid: 'bg-emerald-100 text-emerald-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-slate-100 text-slate-500',
};

export default function AccountingBills({ bills, chartOfAccounts, vendors, isAdmin, loadData }) {
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [payingBill, setPayingBill] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedBills, setSelectedBills] = useState(new Set());

  const emptyForm = {
    vendor_name: '', vendor_email: '', vendor_address: '',
    bill_date: new Date().toISOString().split('T')[0],
    due_date: '', tax_rate: 0, notes: '', status: 'unpaid',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    accounting_account_id: '', division: 'mortgage',
    is_recurring: false, recurring_frequency: 'monthly', recurring_end_date: ''
  };

  const [form, setForm] = useState(emptyForm);
  const [paymentForm, setPaymentForm] = useState({
    amount: '', payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer', reference: '', notes: ''
  });

  const filteredBills = useMemo(() => {
    let result = bills || [];
    if (statusFilter !== 'all') result = result.filter(b => b.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(b =>
        (b.vendor_name || '').toLowerCase().includes(q) ||
        (b.bill_number || '').toLowerCase().includes(q) ||
        (b.notes || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [bills, statusFilter, searchQuery]);

  const totals = useMemo(() => ({
    total: (bills || []).reduce((s, b) => s + (b.total || 0), 0),
    outstanding: (bills || []).filter(b => b.status !== 'paid' && b.status !== 'cancelled').reduce((s, b) => s + (b.balance_due || 0), 0),
    paid: (bills || []).filter(b => b.status === 'paid').reduce((s, b) => s + (b.total || 0), 0),
  }), [bills]);

  const calcSubtotal = () => form.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const calcTax = () => calcSubtotal() * (parseFloat(form.tax_rate) || 0) / 100;
  const calcTotal = () => calcSubtotal() + calcTax();

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, val) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: val };
    setForm({ ...form, items });
  };

  const openNew = () => { setEditingBill(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (bill) => {
    setEditingBill(bill);
    setForm({
      vendor_name: bill.vendor_name, vendor_email: bill.vendor_email || '',
      vendor_address: bill.vendor_address || '', bill_date: bill.bill_date,
      due_date: bill.due_date, tax_rate: bill.tax_rate || 0, notes: bill.notes || '',
      status: bill.status, items: bill.items.length ? bill.items : [{ description: '', quantity: 1, unit_price: 0 }],
      accounting_account_id: bill.accounting_account_id || '', division: bill.division || 'mortgage',
      is_recurring: bill.is_recurring || false,
      recurring_frequency: bill.recurring_frequency || 'monthly',
      recurring_end_date: bill.recurring_end_date || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    try {
      const data = {
        ...form, tax_rate: parseFloat(form.tax_rate) || 0,
        items: form.items.map(i => ({ ...i, quantity: parseFloat(i.quantity) || 1, unit_price: parseFloat(i.unit_price) || 0 })),
        accounting_account_id: form.accounting_account_id || null
      };
      if (editingBill) {
        await axios.put(`${API}/api/accounting/bills/${editingBill.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Bill updated');
      } else {
        await axios.post(`${API}/api/accounting/bills`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Bill created');
      }
      setShowModal(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save bill'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this bill?')) return;
    try {
      await axios.delete(`${API}/api/accounting/bills/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Bill deleted');
      setSelectedBills(prev => { const n = new Set(prev); n.delete(id); return n; });
      loadData();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const toggleSelect = (id) => {
    setSelectedBills(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedBills.size === filteredBills.length) {
      setSelectedBills(new Set());
    } else {
      setSelectedBills(new Set(filteredBills.map(b => b.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedBills.size === 0) return;
    if (!window.confirm(`Delete ${selectedBills.size} selected bill(s)?`)) return;
    try {
      await axios.post(`${API}/api/accounting/bills/bulk-delete`,
        { bill_ids: [...selectedBills] },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`Deleted ${selectedBills.size} bill(s)`);
      setSelectedBills(new Set());
      loadData();
    } catch (err) { toast.error('Failed to delete bills'); }
  };

  const openPayment = (bill) => {
    setPayingBill(bill);
    setPaymentForm({
      amount: bill.balance_due?.toString() || '', payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'bank_transfer', reference: '', notes: ''
    });
    setShowPaymentModal(true);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/accounting/bills/${payingBill.id}/payments`,
        { ...paymentForm, amount: parseFloat(paymentForm.amount) || 0 },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Payment recorded');
      setShowPaymentModal(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record payment'); }
  };

  const downloadPdf = async (billId) => {
    try {
      const res = await axios.get(`${API}/api/accounting/bills/pdf/${billId}`,
        { headers: { Authorization: `Bearer ${getToken()}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Bill_${billId}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { toast.error('Failed to download PDF'); }
  };

  const exportColumns = [
    { key: 'bill_number', label: 'Bill #' },
    { key: 'vendor_name', label: 'Vendor' },
    { key: 'bill_date', label: 'Bill Date' },
    { key: 'due_date', label: 'Due Date' },
    { key: 'subtotal', label: 'Subtotal', format: 'currency' },
    { key: 'tax_amount', label: 'Tax', format: 'currency' },
    { key: 'total', label: 'Total', format: 'currency' },
    { key: 'amount_paid', label: 'Paid', format: 'currency' },
    { key: 'balance_due', label: 'Balance Due', format: 'currency' },
    { key: 'status', label: 'Status' },
    { key: 'division', label: 'Division' },
    { key: 'notes', label: 'Notes' },
  ];

  const expenseAccounts = (chartOfAccounts || []).filter(a => a.account_type === 'expense' && a.is_active);

  return (
    <div className="space-y-4" data-testid="accounting-bills">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Total Bills</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="bills-total">{formatCurrency(totals.total)}</p>
          <p className="text-xs text-slate-400">{(bills || []).length} bills</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Outstanding</p>
          <p className="text-2xl font-bold text-amber-600" data-testid="bills-outstanding">{formatCurrency(totals.outstanding)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Paid</p>
          <p className="text-2xl font-bold text-emerald-600" data-testid="bills-paid">{formatCurrency(totals.paid)}</p>
        </CardContent></Card>
      </div>

      {/* Header */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Bills (Accounts Payable)</CardTitle>
            <CardDescription>{searchQuery ? `${filteredBills.length} of ${(bills || []).length}` : (bills || []).length} bills</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <ExportDropdown filteredData={filteredBills} allData={bills || []} columns={exportColumns}
              filenamePrefix="bills" title="Bills (Accounts Payable)" />
            {isAdmin && <Button variant="outline" onClick={() => setShowImportModal(true)} data-testid="import-bills-btn"><Upload className="w-4 h-4 mr-2" /> Import</Button>}
            {isAdmin && <Button onClick={openNew} data-testid="add-bill-btn"><Plus className="w-4 h-4 mr-2" /> Add Bill</Button>}
          </div>
        </CardHeader>
        <CardContent>
          {/* Search & Filter */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search vendor, bill #, notes..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 pr-9" data-testid="bills-search" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="bills-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="partially_paid">Partially Paid</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Bulk Actions */}
          {isAdmin && selectedBills.size > 0 && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 rounded-lg border" data-testid="bulk-actions-bar">
              <Checkbox checked={selectedBills.size === filteredBills.length && filteredBills.length > 0} onCheckedChange={toggleSelectAll} />
              <span className="text-sm font-medium">{selectedBills.size} selected</span>
              <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedBills(new Set())} data-testid="clear-selection-btn">
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={filteredBills.length > 0 && selectedBills.size === filteredBills.length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-bills"
                      />
                    </TableHead>
                  )}
                  <TableHead>Bill #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Bill Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Division</TableHead>
                  {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBills.length === 0 ? (
                  <TableRow><TableCell colSpan={isAdmin ? 12 : 10} className="text-center py-8 text-slate-500">
                    {searchQuery || statusFilter !== 'all' ? 'No bills match your criteria.' : 'No bills yet. Click "Add Bill" to create one.'}
                  </TableCell></TableRow>
                ) : filteredBills.map(bill => (
                  <TableRow key={bill.id} className="hover:bg-slate-50">
                    {isAdmin && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedBills.has(bill.id)}
                          onCheckedChange={() => toggleSelect(bill.id)}
                          data-testid={`select-bill-${bill.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-medium">{bill.bill_number}</TableCell>
                    <TableCell>{bill.vendor_name}</TableCell>
                    <TableCell>{bill.bill_date}</TableCell>
                    <TableCell>{bill.due_date}</TableCell>
                    <TableCell className="text-right">{formatCurrency(bill.total)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(bill.amount_paid)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(bill.balance_due)}</TableCell>
                    <TableCell><Badge className={statusColors[bill.status] || 'bg-slate-100'}>{bill.status.replace('_', ' ')}</Badge></TableCell>
                    <TableCell className="capitalize">{bill.division}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(bill)} data-testid={`edit-bill-${bill.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {bill.status !== 'paid' && (
                            <Button variant="ghost" size="sm" onClick={() => openPayment(bill)} data-testid={`pay-bill-${bill.id}`}>
                              <DollarSign className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => downloadPdf(bill.id)} data-testid={`download-bill-${bill.id}`}>
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(bill.id)} data-testid={`delete-bill-${bill.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Bill Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingBill ? 'Edit Bill' : 'New Bill'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor Name *</Label>
                <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} required data-testid="bill-vendor-name" />
              </div>
              <div className="space-y-2">
                <Label>Vendor Email</Label>
                <Input type="email" value={form.vendor_email} onChange={(e) => setForm({ ...form, vendor_email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Bill Date *</Label>
                <Input type="date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Due Date *</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Division</Label>
                <Select value={form.division} onValueChange={(v) => setForm({ ...form, division: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mortgage">Mortgage</SelectItem>
                    <SelectItem value="insurance">Insurance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Expense Account</Label>
                <Select value={form.accounting_account_id || 'none'} onValueChange={(v) => setForm({ ...form, accounting_account_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {expenseAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.account_number} - {a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-2">
              <Label>Line Items</Label>
              {form.items.map((item, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <Input className="flex-1" placeholder="Description" value={item.description}
                    onChange={(e) => updateItem(i, 'description', e.target.value)} />
                  <Input className="w-20" type="number" placeholder="Qty" value={item.quantity}
                    onChange={(e) => updateItem(i, 'quantity', e.target.value)} />
                  <Input className="w-28" type="number" step="0.01" placeholder="Price" value={item.unit_price}
                    onChange={(e) => updateItem(i, 'unit_price', e.target.value)} />
                  <span className="w-24 text-right text-sm leading-10">{formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}</span>
                  {form.items.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)}><Trash2 className="w-4 h-4 text-red-400" /></Button>}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input type="number" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} />
              </div>
              <div className="space-y-2 text-right pt-2">
                <div className="text-sm text-slate-500">Subtotal: {formatCurrency(calcSubtotal())}</div>
                <div className="text-sm text-slate-500">Tax: {formatCurrency(calcTax())}</div>
                <div className="text-lg font-bold">Total: {formatCurrency(calcTotal())}</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-bill-btn">{editingBill ? 'Update Bill' : 'Create Bill'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Payment — {payingBill?.bill_number}</DialogTitle></DialogHeader>
          <form onSubmit={handlePayment} className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Total:</span><span className="font-medium">{formatCurrency(payingBill?.total)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Paid:</span><span>{formatCurrency(payingBill?.amount_paid)}</span></div>
              <div className="flex justify-between font-bold"><span>Balance Due:</span><span className="text-amber-600">{formatCurrency(payingBill?.balance_due)}</span></div>
            </div>
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input type="number" step="0.01" value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} required data-testid="bill-payment-amount" />
            </div>
            <div className="space-y-2">
              <Label>Payment Date *</Label>
              <Input type="date" value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reference</Label>
              <Input value={paymentForm.reference} onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })} placeholder="Check #, transaction ID..." />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
              <Button type="submit" data-testid="record-bill-payment-btn">Record Payment</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <AccountingImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        importType="bill"
        onImportComplete={loadData}
      />
    </div>
  );
}
